import React, { useState, useEffect, useRef, useMemo } from 'react'
import { BrowserRouter as Router, Routes, Route, useNavigate } from 'react-router-dom'
import { Menu, X, Layers, ChevronDown, BarChart } from 'lucide-react'

import AuthModal from './components/AuthModal'
import Dashboard from './pages/Dashboard'
import Integrations from './components/Integrations'
import BetaWaitlist from './pages/BetaWaitlist'
import Contact from './pages/Contact'
import Terms from './pages/Terms'
import Privacy from './pages/Privacy'
import Security from './pages/Security'
import Pricing from './pages/Pricing'
import Footer from './components/Footer'
import AnimatedBeamDemo from './components/animated-beam-demo'
import TiccAIlogo from './assets/TiccAIlogo.png'
import VideoModal from './components/VideoModal'
import { GlowingEffectDemo } from './components/GlowingEffectDemo'
import TiccAIlogoWhite from './assets/TiccAIlogoWhite.png'

const MemoizedAnimatedBeamDemo = React.memo(AnimatedBeamDemo);

const Particles = () => {
  const particles = useMemo(() => {
    return new Array(30).fill(null).map((_, i) => {
      const isBlue = Math.random() > 0.5;
      const color = isBlue ? '147, 197, 253' : '167, 139, 250';
      const size = Math.random() * 6 + 3;
      const left = Math.random() * 100;
      const top = Math.random() * 70;
      const delay = Math.random() * 10;
      const duration = Math.random() * 15 + 20;
      
      return {
        key: i,
        isBlue,
        color,
        size,
        left,
        top,
        delay,
        duration
      };
    });
  }, []); // Empty dependency array ensures this only runs once

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {particles.map(({ key, color, size, left, top, delay, duration }) => (
        <div
          key={key}
          className="particle absolute rounded-full opacity-[0.4]"
          style={{
            width: `${size}px`,
            height: `${size}px`,
            background: `radial-gradient(circle at center, rgba(${color}, 0.8) 0%, rgba(${color}, 0.2) 50%, rgba(${color}, 0) 100%)`,
            boxShadow: `0 0 ${size * 2}px ${size/2}px rgba(${color}, 0.3)`,
            left: `${left}%`,
            top: `${top}%`,
            animationDelay: `${delay}s`,
            animationDuration: `${duration}s`,
            transform: 'translate3d(0, 0, 0)',
            backdropFilter: 'blur(0.5px)'
          }}
        />
      ))}
    </div>
  );
};

// Add the counting animation hook
const useCountAnimation = (end: number, duration: number = 1000, delay: number = 0) => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const elementRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (elementRef.current) {
      observer.observe(elementRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;

    const startTime = performance.now();
    const startValue = 0;

    const animate = (currentTime: number) => {
      const elapsedTime = currentTime - startTime;
      const progress = Math.min(elapsedTime / duration, 1);
      const currentValue = Math.floor(startValue + (end - startValue) * progress);
      setCount(currentValue);

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    const timeoutId = setTimeout(() => {
      requestAnimationFrame(animate);
    }, delay);

    return () => clearTimeout(timeoutId);
  }, [isVisible, end, duration, delay]);

  return { count, elementRef };
};

// Format number with K suffix
const formatNumber = (num: number) => {
  if (num === 65) {
    return `${num}%`;
  }
  if (num === 1500) {
    return `${num.toLocaleString()}+`;
  }
  return num.toString();
};

function HomePage() {
  const navigate = useNavigate();
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const [openFaq, setOpenFaq] = useState<string | null>(null);
  const [currentFeatureIndex, setCurrentFeatureIndex] = useState(0);
  const carouselRef = useRef<HTMLDivElement>(null);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [isScrolling, setIsScrolling] = useState(false);
  const scrollTimeout = useRef<NodeJS.Timeout>();
  const [maxScrollPosition, setMaxScrollPosition] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);

  // Add useEffect for page loaded class
  useEffect(() => {
    if (document.readyState === 'complete') {
      document.body.classList.add('page-loaded');
    } else {
      window.addEventListener('load', () => {
        document.body.classList.add('page-loaded');
      });
    }
  }, []);

  useEffect(() => {
    const calculateMaxScroll = () => {
      if (carouselRef.current) {
        const carouselWidth = carouselRef.current.scrollWidth;
        const viewportWidth = window.innerWidth;
        const padding = 240; // This is the padding-left value from your CSS
        const maxScroll = carouselWidth - viewportWidth + padding;
        setMaxScrollPosition(Math.max(0, maxScroll));
      }
    };

    calculateMaxScroll();
    window.addEventListener('resize', calculateMaxScroll);
    return () => window.removeEventListener('resize', calculateMaxScroll);
  }, []);

  useEffect(() => {
    if (carouselRef.current) {
      // This will trigger the transform with a scrollPosition > 0 and snap back
      setScrollPosition(1);
      requestAnimationFrame(() => {
        setScrollPosition(0); // Snap back
      });
    }
  }, [maxScrollPosition]);

  const [responsivePadding, setResponsivePadding] = useState(240);

  useEffect(() => {
    const getResponsivePadding = () => {
      const screenWidth = window.innerWidth;
      if (screenWidth < 640) return 16;   // small screens
      if (screenWidth < 768) return 64;   // tablets
      if (screenWidth < 1024) return 96;  // small laptops
      return 240;                         // desktops
    };

    const updatePadding = () => setResponsivePadding(getResponsivePadding());

    updatePadding(); // initial run
    window.addEventListener('resize', updatePadding);
    return () => window.removeEventListener('resize', updatePadding);
  }, []);

  useEffect(() => {
    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    });

    // Observe all fade-up elements and the stats section
    document.querySelectorAll('.fade-up-on-scroll, .float-in-on-scroll, .typing-text, .fade-right-to-left, .stats-section').forEach(element => {
      observer.observe(element);
    });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const carouselEl = carouselRef.current;
    if (!carouselEl) return;
  
    const wheelHandler = (e: WheelEvent) => {
      const isHorizontal = Math.abs(e.deltaX) > Math.abs(e.deltaY);

      if (isHorizontal) e.preventDefault(); // always cancel horizontal swipe
      handleScroll(e.deltaX);               // call even at scroll edge (no‑op if clamped)
    };
  
    carouselEl.addEventListener('wheel', wheelHandler, { passive: false });
  
    return () => {
      carouselEl.removeEventListener('wheel', wheelHandler);
    };
  }, [scrollPosition, currentFeatureIndex]);
  

  const openAuthModal = (mode: 'signup' | 'signin') => {
    setIsAuthModalOpen(true);
  }

  const closeAuthModal = () => {
    setIsAuthModalOpen(false);
  }  

  const handleScroll = (deltaX: number) => {
    if (scrollTimeout.current) {
      clearTimeout(scrollTimeout.current);
    }
  
    setIsScrolling(true);
  
    setScrollPosition(prev => {
      const newPosition = Math.max(0, Math.min(maxScrollPosition, prev + deltaX * 0.3));
      const newIndex = Math.round(newPosition / 550);
  
      if (newIndex !== currentFeatureIndex) {
        setCurrentFeatureIndex(newIndex);
      }
  
      return newPosition;
    });
  
    scrollTimeout.current = setTimeout(() => {
      setIsScrolling(false);
    }, 150);
  };
  
  const handlePrevClick = () => {
    if (currentFeatureIndex > 0) {
      setCurrentFeatureIndex(prev => prev - 1);
      setScrollPosition(prev => {
        const newPosition = Math.max(0, prev - 550);
        return newPosition;
      });
    }
  };

  const handleNextClick = () => {
    if (currentFeatureIndex < features.length - 1) {
      setCurrentFeatureIndex(prev => prev + 1);
      setScrollPosition(prev => {
        const newPosition = Math.min(maxScrollPosition, prev + 550);
        return newPosition;
      });
    }
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.touches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.touches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe && currentFeatureIndex < features.length - 1) {
      handleNextClick();
    } else if (isRightSwipe && currentFeatureIndex > 0) {
      handlePrevClick();
    }

    setTouchStart(0);
    setTouchEnd(0);
  };

  const features = [
    {
      title: "Smart Ticket Analysis",
      description: "Automatically group and analyze similar incidents to identify patterns, reduce noise, and accelerate resolution.",
      mockup: (
        <div className="h-[300px] p-6 -mt-2">
          <div className="bg-[#0A0F1A] rounded-t-lg p-2 flex items-center gap-2 shadow-[0_4px_12px_rgba(0,0,0,0.3)] border border-white/[0.05]">
            <div className="flex gap-1.5">
              <div className="w-2.5 h-2.5 rounded-full bg-red-500/80"></div>
              <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/80"></div>
              <div className="w-2.5 h-2.5 rounded-full bg-green-500/80"></div>
            </div>
          </div>
          <div className="bg-[#0A0F1A] rounded-b-lg p-4 shadow-[0_4px_12px_rgba(0,0,0,0.3)] border border-white/[0.05]">
            {/* Header */}
            <div className="border-b border-white/[0.05] pb-4 mb-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="bg-gradient-to-r from-red-500/10 to-red-500/5 px-3 py-1.5 rounded-lg border border-red-500/10">
                    <span className="text-red-400 font-mono text-sm bg-gradient-to-r from-red-400 to-red-500 bg-clip-text text-transparent">#GRP-2024-042</span>
                  </div>
                  <div className="flex gap-2">
                    <span className="bg-gradient-to-r from-red-500/10 to-red-500/5 text-red-400 px-2 py-0.5 rounded-full text-xs font-medium border border-red-500/10">P0 - Critical</span>
                    <span className="bg-gradient-to-r from-red-500/10 to-red-500/5 text-red-400 px-2 py-0.5 rounded-full text-xs font-medium border border-red-500/10">6 Active</span>
                  </div>
                </div>
                <div className="text-gray-400 text-xs font-medium bg-white/5 px-2 py-1 rounded-md backdrop-blur-sm">First occurred 15m ago</div>
              </div>
              <h3 className="text-base font-medium text-transparent bg-gradient-to-r from-gray-200 to-gray-100 bg-clip-text mb-3">Database Connection Pool Exhaustion</h3>
              <div className="flex gap-2">
                <span className="bg-gradient-to-r from-blue-500/10 to-blue-600/5 text-blue-400 px-2 py-0.5 rounded-full text-xs font-medium border border-blue-500/10">High Priority</span>
                <span className="bg-gradient-to-r from-purple-500/10 to-purple-600/5 text-purple-400 px-2 py-0.5 rounded-full text-xs font-medium border border-purple-500/10">4 Services Affected</span>
                <span className="bg-gradient-to-r from-amber-500/10 via-orange-500/10 to-orange-600/5 text-amber-400 px-2 py-0.5 rounded-full text-xs font-medium border border-amber-500/10">MTTR: 45m</span>
              </div>
            </div>

            {/* Main Content - Horizontal Layout */}
            <div className="flex gap-6 h-[calc(100%-120px)]">
              {/* Left Column - Grouped Incidents */}
              <div className="w-1/4">
                <div className="text-xs font-medium text-gray-400 mb-3 flex items-center gap-2">
                  <span>Grouped Incidents</span>
                  <span className="bg-gradient-to-r from-indigo-500/20 to-indigo-600/10 text-indigo-400 px-1.5 py-0.5 rounded-md text-[10px] font-medium">6</span>
                </div>
                <div className="h-[calc(100%-24px)] relative">
                  <div className="space-y-2">
                    <div className="bg-gradient-to-r from-[#0A1420]/80 to-[#0D1824]/80 backdrop-blur-sm p-3 rounded-lg border border-white/[0.03] hover:border-white/[0.05] transition-all duration-300">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-mono text-xs text-gray-300">#INC-2024-042</span>
                        <div className="flex items-center gap-1.5">
                          <div className="w-1 h-1 rounded-full bg-green-500 animate-pulse"></div>
                          <span className="text-green-400 text-xs font-medium">98%</span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="text-xs text-gray-300">User Auth API</div>
                        <span className="bg-gradient-to-r from-red-500/10 to-red-600/5 text-red-400 px-1.5 py-0.5 rounded-full text-[10px] font-medium border border-red-500/10">P0</span>
                      </div>
                      <div className="text-[10px] text-gray-500 mt-1.5">2m ago • 3 retries</div>
                    </div>
                    <div className="bg-gradient-to-r from-[#0A1420]/80 to-[#0D1824]/80 backdrop-blur-sm p-3 rounded-lg border border-white/[0.03] hover:border-white/[0.05] transition-all duration-300">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-mono text-xs text-gray-300">#INC-2024-043</span>
                        <div className="flex items-center gap-1.5">
                          <div className="w-1 h-1 rounded-full bg-green-500 animate-pulse"></div>
                          <span className="text-green-400 text-xs font-medium">95%</span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="text-xs text-gray-300">Payment Gateway</div>
                        <span className="bg-gradient-to-r from-red-500/10 to-red-600/5 text-red-400 px-1.5 py-0.5 rounded-full text-[10px] font-medium border border-red-500/10">P0</span>
                      </div>
                      <div className="text-[10px] text-gray-500 mt-1.5">8m ago • 5 retries</div>
                    </div>
                    <div className="relative">
                      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0A1420]/80 to-[#0A1420] pointer-events-none"></div>
                      <div className="bg-gradient-to-r from-[#0A1420]/80 to-[#0D1824]/80 backdrop-blur-sm p-3 rounded-lg border border-white/[0.03] hover:border-white/[0.05] transition-all duration-300">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-mono text-xs text-gray-300">#INC-2024-044</span>
                          <div className="flex items-center gap-1.5">
                            <div className="w-1 h-1 rounded-full bg-green-500 animate-pulse"></div>
                            <span className="text-green-400 text-xs font-medium">92%</span>
                          </div>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="text-xs text-gray-300">Order Processing</div>
                          <span className="bg-gradient-to-r from-orange-500/10 to-orange-600/5 text-orange-400 px-1.5 py-0.5 rounded-full text-[10px] font-medium border border-orange-500/10">P1</span>
                        </div>
                        <div className="text-[10px] text-gray-500 mt-1.5">12m ago • 2 retries</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column - Analysis */}
              <div className="w-3/4">
                <div className="text-xs font-medium text-gray-400 mb-3 flex items-center gap-2">
                  <span>Analysis</span>
                  <div className="w-1 h-1 rounded-full bg-blue-500 animate-pulse"></div>
                </div>
                <div className="grid grid-cols-2 gap-4 h-[calc(100%-24px)]">
                  <div className="bg-gradient-to-r from-[#0A1420]/80 to-[#0D1824]/80 backdrop-blur-sm p-4 rounded-lg border border-white/[0.03] hover:border-white/[0.05] transition-all duration-300 flex flex-col relative group">
                    <div className="absolute inset-0 bg-gradient-to-r from-orange-500/5 to-orange-600/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-lg"></div>
                    <h4 className="text-xs font-medium text-gray-300 mb-3 relative">Common Pattern</h4>
                    <div className="flex flex-wrap gap-2 mb-3 relative">
                      <span className="bg-gradient-to-r from-rose-500/20 via-pink-500/10 to-orange-500/5 text-rose-300 px-2 py-0.5 rounded-full text-[10px] font-medium border border-rose-500/10 shadow-[0_0_12px_-3px_rgba(244,63,94,0.3)] backdrop-blur-sm hover:border-rose-500/20 transition-all duration-300">connection_pool</span>
                      <span className="bg-gradient-to-r from-fuchsia-500/20 via-pink-500/10 to-rose-500/5 text-fuchsia-300 px-2 py-0.5 rounded-full text-[10px] font-medium border border-fuchsia-500/10 shadow-[0_0_12px_-3px_rgba(217,70,239,0.3)] backdrop-blur-sm hover:border-fuchsia-500/20 transition-all duration-300">deadlock</span>
                      <span className="bg-gradient-to-r from-violet-500/20 via-fuchsia-500/10 to-pink-500/5 text-violet-300 px-2 py-0.5 rounded-full text-[10px] font-medium border border-violet-500/10 shadow-[0_0_12px_-3px_rgba(139,92,246,0.3)] backdrop-blur-sm hover:border-violet-500/20 transition-all duration-300">thread_starvation</span>
                      <span className="bg-gradient-to-r from-purple-500/20 via-violet-500/10 to-fuchsia-500/5 text-purple-300 px-2 py-0.5 rounded-full text-[10px] font-medium border border-purple-500/10 shadow-[0_0_12px_-3px_rgba(168,85,247,0.3)] backdrop-blur-sm hover:border-purple-500/20 transition-all duration-300">high_wait_time</span>
                    </div>
                    <div className="space-y-3 flex-grow relative">
                      <p className="text-xs text-gray-400">Connection pool exhaustion with thread contention causing cascading timeouts across services</p>
                      <div className="mt-3 space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] text-gray-500">Active Connections</span>
                          <span className="text-[10px] text-red-400 font-medium">198/200 (99%)</span>
                        </div>
                        <div className="h-1 bg-red-500/10 rounded-full overflow-hidden">
                          <div className="h-full w-[99%] bg-gradient-to-r from-red-500 to-red-400 rounded-full"></div>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] text-gray-500">Avg Wait Time</span>
                          <span className="text-[10px] text-orange-400 font-medium">2.8s (↑ from 150ms)</span>
                        </div>
                        <div className="h-1 bg-orange-500/10 rounded-full overflow-hidden">
                          <div className="h-full w-[75%] bg-gradient-to-r from-orange-500 to-orange-400 rounded-full"></div>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] text-gray-500">Connection Leaks</span>
                          <span className="text-[10px] text-red-400 font-medium">12 detected</span>
                        </div>
                        <div className="h-1 bg-red-500/10 rounded-full overflow-hidden">
                          <div className="h-full w-[60%] bg-gradient-to-r from-red-500 to-red-400 rounded-full"></div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-[#0A1420]/80 to-[#0D1824]/80 backdrop-blur-sm p-4 rounded-lg border border-white/[0.03] hover:border-white/[0.05] transition-all duration-300 flex flex-col relative group">
                    <div className="absolute inset-0 bg-gradient-to-r from-red-500/5 to-red-600/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-lg"></div>
                    <h4 className="text-xs font-medium text-gray-300 mb-3 relative">Affected Services</h4>
                    <div className="space-y-3 flex-grow relative">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-gradient-to-r from-red-500 to-red-400 animate-pulse"></div>
                          <span className="text-xs text-gray-300">User Auth API</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="h-1 w-16 bg-red-500/10 rounded-full overflow-hidden">
                            <div className="h-full w-[80%] bg-gradient-to-r from-red-500 to-red-400 rounded-full"></div>
                          </div>
                          <span className="text-[10px] text-gray-400">52 waiting</span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-gradient-to-r from-red-500 to-red-400 animate-pulse"></div>
                          <span className="text-xs text-gray-300">Payment Gateway</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="h-1 w-16 bg-red-500/10 rounded-full overflow-hidden">
                            <div className="h-full w-[70%] bg-gradient-to-r from-red-500 to-red-400 rounded-full"></div>
                          </div>
                          <span className="text-[10px] text-gray-400">45 waiting</span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-gradient-to-r from-red-500 to-red-400 animate-pulse"></div>
                          <span className="text-xs text-gray-300">Order Processing</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="h-1 w-16 bg-red-500/10 rounded-full overflow-hidden">
                            <div className="h-full w-[60%] bg-gradient-to-r from-red-500 to-red-400 rounded-full"></div>
                          </div>
                          <span className="text-[10px] text-gray-400">38 waiting</span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-gradient-to-r from-red-500 to-red-400 animate-pulse"></div>
                          <span className="text-xs text-gray-300">Customer Dashboard</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="h-1 w-16 bg-red-500/10 rounded-full overflow-hidden">
                            <div className="h-full w-[50%] bg-gradient-to-r from-red-500 to-red-400 rounded-full"></div>
                          </div>
                          <span className="text-[10px] text-gray-400">31 waiting</span>
                        </div>
                      </div>
                      <div className="mt-3 pt-3 border-t border-white/[0.05]">
                        <div className="text-[10px] text-gray-400 mb-2 font-medium">Connection Pool Stats</div>
                        <div className="text-[10px] text-gray-500 space-y-1">
                          <div className="flex items-center justify-between">
                            <span>Min: 50</span>
                            <span>Max: 200</span>
                            <span>Idle: 2</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span>Checkout Timeout: 3s</span>
                            <span className="text-red-400">Critical</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Automated Resolution",
      description: "Other tools summarize. TiccAI goes further, using your docs, GitHub, monitoring tools, and codebase to help you resolve tickets automatically, when applicable.",
      mockup: (
        <div className="w-full h-[calc(100%-6rem)] p-4 flex flex-col">
          <div className="bg-[#0A0F1A] rounded-t-lg p-2 flex items-center gap-2 shadow-[0_4px_12px_rgba(0,0,0,0.3)] border border-white/[0.05]">
            <div className="flex gap-1.5">
              <div className="w-2.5 h-2.5 rounded-full bg-red-500/80"></div>
              <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/80"></div>
              <div className="w-2.5 h-2.5 rounded-full bg-green-500/80"></div>
            </div>
          </div>
          <div className="bg-[#0A0F1A] rounded-b-lg p-4 flex shadow-[0_4px_12px_rgba(0,0,0,0.3)] border border-white/[0.05] h-full">
            {/* Left Panel - Incident Details */}
            <div className="w-1/4 pr-4 border-r border-white/[0.05]">
              <div className="flex items-center justify-between mb-4">
                <div className="text-sm font-medium text-gray-300">#INC-2024-042</div>
                <div className="px-2 py-1 rounded-full bg-red-500/10 text-red-400 text-xs font-medium">P0</div>
              </div>
              <div className="space-y-3">
                <div className="bg-[#0A1420]/80 backdrop-blur-sm rounded-lg p-3 border border-white/[0.03] hover:border-white/[0.05] transition-all duration-300">
                  <div className="text-xs text-gray-400 mb-1">Service</div>
                  <div className="text-sm text-white font-medium">Database Service</div>
                  <div className="text-xs text-gray-500 mt-1">First seen 2m ago</div>
                </div>

                {/* AI Summary Button with Gradient and Glow */}
                <button className="w-full bg-gradient-to-r from-pink-500 via-purple-500 to-pink-600 p-[1px] rounded-lg relative group transition-all">
                  <div className="bg-[#0A1420]/90 backdrop-blur-sm rounded-[7px] p-3 relative overflow-hidden">
                    {/* Animated gradient background */}
                    <div className="absolute inset-0 bg-gradient-to-r from-pink-500/20 via-purple-500/20 to-pink-600/20 opacity-0"></div>
                    
                    {/* Content */}
                    <div className="relative flex items-center justify-between">
                      <div>
                        <div className="text-white font-medium mb-0.5 flex items-center gap-2">
                          <span>View AI Summary</span>
                        </div>
                        <div className="text-xs text-gray-400 opacity-75">Analysis and recommendations</div>
                      </div>
                    </div>
                  </div>
                </button>

                {/* Resolution Progress */}
                <div className="bg-[#0A1420]/80 backdrop-blur-sm rounded-lg p-3 border border-white/[0.03]">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs text-gray-400">Resolution Progress</div>
                    <div className="text-xs font-medium text-emerald-400">75%</div>
                  </div>
                  <div className="h-1.5 bg-white/[0.03] rounded-full overflow-hidden">
                    <div className="h-full w-3/4 bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-full"></div>
                  </div>
                  <div className="mt-2 text-[10px] text-gray-500">Estimated time: 2m remaining</div>
                </div>
              </div>
            </div>

            {/* Middle Panel - Resolution Steps */}
            <div className="w-2/4 px-4 border-r border-white/[0.05]">
              <div className="flex items-center justify-between mb-4">
                <div className="text-sm font-medium text-gray-300">Resolution Steps</div>
                <div className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></div>
                  <div className="text-xs text-emerald-400 font-medium">In Progress</div>
                </div>
              </div>

              <div className="space-y-3">
                {/* Completed Step */}
                <div className="bg-[#0A1420]/80 backdrop-blur-sm rounded-lg p-3 border border-emerald-500/20">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 rounded-full bg-emerald-500/10 flex items-center justify-center">
                        <svg className="w-3 h-3 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <div className="text-sm text-emerald-400 font-medium">Analyze Connection Pool</div>
                    </div>
                    <div className="text-xs text-gray-400">Completed</div>
                  </div>
                  <div className="pl-7 text-xs text-gray-400">Identified high utilization (95%) and connection leaks</div>
                </div>

                {/* Current Step */}
                <div className="bg-[#0A1420]/80 backdrop-blur-sm rounded-lg p-3 border border-white/[0.03] relative group">
                  <div className="absolute inset-0 bg-gradient-to-r from-pink-500/5 via-purple-500/5 to-pink-600/5 rounded-lg animate-pulse"></div>
                  <div className="relative">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className="w-5 h-5 rounded-full bg-pink-500/10 flex items-center justify-center">
                          <div className="w-2 h-2 rounded-full bg-pink-500 animate-pulse"></div>
                        </div>
                        <div className="text-sm text-white font-medium">Scale Connection Pool</div>
                      </div>
                      <div className="text-xs text-pink-400">In Progress</div>
                    </div>
                    <div className="pl-7 space-y-3">
                      <div className="text-xs text-gray-400">Increasing max connections from 200 to 300</div>
                      <div className="h-1.5 bg-white/[0.03] rounded-full overflow-hidden">
                        <div className="h-full w-1/2 bg-gradient-to-r from-pink-500 to-purple-500 rounded-full"></div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Pending Step */}
                <div className="bg-[#0A1420]/80 backdrop-blur-sm rounded-lg p-3 border border-white/[0.03] opacity-60">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 rounded-full bg-white/[0.05] flex items-center justify-center">
                        <div className="w-2 h-2 rounded-full bg-white/20"></div>
                      </div>
                      <div className="text-sm text-gray-300 font-medium">Update Health Checks</div>
                    </div>
                    <div className="text-xs text-gray-400">Pending</div>
                  </div>
                  <div className="pl-7 text-xs text-gray-500">Configure new thresholds and alerts</div>
                </div>
              </div>
            </div>

            {/* Right Panel - System Status */}
            <div className="w-1/4 pl-4">
              <div className="flex items-center justify-between mb-4">
                <div className="text-sm font-medium text-gray-300">System Status</div>
                <div className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-yellow-500"></div>
                  <div className="text-xs text-yellow-400 font-medium">Stabilizing</div>
                </div>
              </div>

              <div className="space-y-3">
                {/* Connection Pool Status */}
                <div className="bg-[#0A1420]/80 backdrop-blur-sm rounded-lg p-3 border border-white/[0.03]">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-sm text-white font-medium">Connection Pool</div>
                    <div className="text-xs text-yellow-400">85%</div>
                  </div>
                  <div className="h-1.5 bg-white/[0.03] rounded-full overflow-hidden">
                    <div className="h-full w-[85%] bg-gradient-to-r from-yellow-500 to-yellow-400 rounded-full"></div>
                  </div>
                  <div className="flex justify-between mt-2">
                    <div className="text-[10px] text-gray-500">Active: 255</div>
                    <div className="text-[10px] text-gray-500">Max: 300</div>
                  </div>
                </div>

                {/* Response Time */}
                <div className="bg-[#0A1420]/80 backdrop-blur-sm rounded-lg p-3 border border-white/[0.03]">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-sm text-white font-medium">Response Time</div>
                    <div className="text-xs text-emerald-400">1.2s</div>
                  </div>
                  <div className="h-1.5 bg-white/[0.03] rounded-full overflow-hidden">
                    <div className="h-full w-[40%] bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-full"></div>
                  </div>
                  <div className="flex justify-between mt-2">
                    <div className="text-[10px] text-gray-500">p95</div>
                    <div className="text-[10px] text-emerald-400">↓ 48%</div>
                  </div>
                </div>

                {/* Error Rate */}
                <div className="bg-[#0A1420]/80 backdrop-blur-sm rounded-lg p-3 border border-white/[0.03]">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-sm text-white font-medium">Error Rate</div>
                    <div className="text-xs text-emerald-400">2.5%</div>
                  </div>
                  <div className="h-1.5 bg-white/[0.03] rounded-full overflow-hidden">
                    <div className="h-full w-[25%] bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-full"></div>
                  </div>
                  <div className="flex justify-between mt-2">
                    <div className="text-[10px] text-gray-500">Last 5m</div>
                    <div className="text-[10px] text-emerald-400">↓ 80%</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Knowledge Integration",
      description: "No more clicking through Jira, scrolling Slack, or searching docs. We connect the dots and resolve issues using what your team already knows.",
      mockup: (
        <div className="w-full h-[300px] bg-[#070d17] p-4 flex flex-col">
          <div className="bg-[#0A0F1A] rounded-t-lg p-2 flex items-center gap-2 shadow-[0_4px_12px_rgba(0,0,0,0.3)] border border-white/[0.05]">
            <div className="flex gap-1.5">
              <div className="w-2.5 h-2.5 rounded-full bg-red-500/80"></div>
              <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/80"></div>
              <div className="w-2.5 h-2.5 rounded-full bg-green-500/80"></div>
            </div>
          </div>
          <div className="bg-[#0A0F1A] rounded-b-lg p-4 flex flex-col shadow-[0_4px_12px_rgba(0,0,0,0.3)] border border-white/[0.05] flex-1">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="text-sm font-medium text-gray-300">#INC-2024-042</div>
                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                <div className="text-xs text-gray-400">Analyzing</div>
              </div>
              <div className="text-xs font-medium text-gray-300">DB Connection Pool Exhaustion</div>
            </div>

            <div className="grid grid-cols-2 gap-4 flex-1">
              <div className="space-y-4">
                <div className="bg-[#0A1420]/80 backdrop-blur-sm rounded-xl p-4 border border-white/[0.03] hover:border-white/[0.05] transition-all duration-300">
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-xs font-medium text-gray-300">Past Similar Incidents</div>
                    <div className="w-1.5 h-1.5 rounded-full bg-green-500/50"></div>
                  </div>
                  <div className="space-y-3">
                    <div className="bg-[#0D1A2F]/50 rounded-lg p-3 border border-white/[0.02] hover:border-white/[0.04] transition-all duration-300">
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-xs font-medium text-gray-300">DB Connection Guide</div>
                        <div className="text-[10px] font-medium text-green-400/80">92% match</div>
                      </div>
                      <div className="space-y-2">
                        <div className="h-2.5 bg-gray-700/20 rounded-full w-3/4 animate-pulse"></div>
                        <div className="h-2.5 bg-gray-700/20 rounded-full w-2/3 animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                        <div className="h-2.5 bg-gray-700/20 rounded-full w-1/2 animate-pulse" style={{ animationDelay: '0.4s' }}></div>
                      </div>
                      <div className="text-[10px] text-gray-500 mt-2">Last updated: 2d ago</div>
                    </div>
                  </div>
                </div>

                <div className="bg-[#0A1420]/80 backdrop-blur-sm rounded-xl p-4 border border-white/[0.03] hover:border-white/[0.05] transition-all duration-300">
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-xs font-medium text-gray-300">Integration Status</div>
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-500/50"></div>
                  </div>
                  <div className="space-y-2">
                    {[
                      { name: 'Slack', items: '3 threads', time: '2m ago', color: 'bg-[#4A154B]/20' },
                      { name: 'Confluence', items: '8 docs', time: '5m ago', color: 'bg-[#0052CC]/20' },
                      { name: 'Jira', items: '2 tickets', time: '1m ago', color: 'bg-[#0052CC]/20' },
                    ].map((service) => (
                      <div key={service.name} className="bg-[#0D1A2F]/50 rounded-lg p-2.5 border border-white/[0.02] hover:border-white/[0.04] transition-all duration-300">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className={`w-1.5 h-1.5 rounded-full ${service.color}`}></div>
                            <span className="text-xs font-medium text-gray-300">{service.name}</span>
                          </div>
                          <span className="text-[10px] text-gray-400">{service.items}</span>
                        </div>
                        <div className="text-[10px] text-gray-500 mt-1 ml-3.5">{service.time}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="bg-[#0A1420]/80 backdrop-blur-sm rounded-xl p-4 border border-white/[0.03] hover:border-white/[0.05] transition-all duration-300">
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-xs font-medium text-gray-300">Recent Activity</div>
                    <div className="w-1.5 h-1.5 rounded-full bg-purple-500/50"></div>
                  </div>
                  <div className="space-y-2">
                    <div className="bg-[#0D1A2F]/50 rounded-lg p-3 border border-white/[0.02] hover:border-white/[0.04] transition-all duration-300">
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-xs font-medium text-gray-300">Connection Pool Update</div>
                        <div className="text-[10px] font-medium text-green-400/80">1h ago</div>
                      </div>
                      <div className="space-y-2">
                        <div className="h-2.5 bg-gray-700/20 rounded-full w-4/5 animate-pulse"></div>
                        <div className="h-2.5 bg-gray-700/20 rounded-full w-3/4 animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-[#0A1420]/80 backdrop-blur-sm rounded-xl p-4 border border-white/[0.03] hover:border-white/[0.05] transition-all duration-300">
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-xs font-medium text-gray-300">Related Incidents</div>
                    <div className="w-1.5 h-1.5 rounded-full bg-orange-500/50"></div>
                  </div>
                  <div className="space-y-2">
                    <div className="bg-[#0D1A2F]/50 rounded-lg p-3 border border-white/[0.02] hover:border-white/[0.04] transition-all duration-300">
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-xs font-medium text-gray-300">#INC-2024-041</div>
                        <div className="text-[10px] font-medium text-orange-400/80">Similar</div>
                      </div>
                      <div className="space-y-2">
                        <div className="h-2.5 bg-gray-700/20 rounded-full w-2/3 animate-pulse"></div>
                        <div className="h-2.5 bg-gray-700/20 rounded-full w-1/2 animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Unified Integration Hub",
      description: "It's not just one signal. TiccAI pulls from all your tools to give complete, actionable context in one place, combining your workflow.",
      mockup: (
        <div className="w-full h-[300px] bg-[#070d17] p-4 flex flex-col">
          <div className="relative w-full h-full flex items-center justify-center">
            {/* Background grid of greyed-out app icons */}
            <div className="absolute inset-0 grid grid-cols-4 grid-rows-3 gap-4 z-0">
              {[...Array(12)].map((_, i) => (
                i === 0 ? (
                  <div
                    key={i}
                    className="flex items-center justify-center rounded-lg bg-gray-800/60"
                    style={{ filter: 'blur(0.5px)', opacity: 0.35 }}
                  >
                    {/* Slack logo SVG in darker grey */}
                    <svg className="w-8 h-8" viewBox="0 0 2447.6 2452.5" xmlns="http://www.w3.org/2000/svg">
                      <g clipRule="evenodd" fillRule="evenodd">
                        <path d="m897.4 0c-135.3.1-244.8 109.9-244.7 245.2-.1 135.3 109.5 245.1 244.8 245.2h244.8v-245.1c.1-135.3-109.5-245.1-244.9-245.3.1 0 .1 0 0 0m0 654h-652.6c-135.3.1-244.9 109.9-244.8 245.2-.2 135.3 109.4 245.1 244.7 245.3h652.7c135.3-.1 244.9-109.9 244.8-245.2.1-135.4-109.5-245.2-244.8-245.3z" fill="#555" />
                        <path d="m2447.6 899.2c.1-135.3-109.5-245.1-244.8-245.2-135.3.1-244.9 109.9-244.8 245.2v245.3h244.8c135.3-.1 244.9-109.9 244.8-245.3zm-652.7 0v-654c.1-135.2-109.4-245-244.7-245.2-135.3.1-244.9 109.9-244.8 245.2v654c-.2 135.3 109.4 245.1 244.7 245.3 135.3-.1 244.9-109.9 244.8-245.3z" fill="#555" />
                        <path d="m1550.1 2452.5c135.3-.1 244.9-109.9 244.8-245.2.1-135.3-109.5-245.1-244.8-245.2h-244.8v245.2c-.1 135.2 109.5 245 244.8 245.2zm0-654.1h652.7c135.3-.1 244.9-109.9 244.8-245.2.2-135.3-109.4-245.1-244.7-245.3h-652.7c-135.3.1-244.9 109.9-244.8 245.2-.1 135.4 109.4 245.2 244.7 245.3z" fill="#555" />
                        <path d="m0 1553.2c-.1 135.3 109.5 245.1 244.8 245.2 135.3-.1 244.9-109.9 244.8-245.2v-245.2h-244.8c-135.3.1-244.9 109.9-244.8 245.2zm652.7 0v654c-.2 135.3 109.4 245.1 244.7 245.3 135.3-.1 244.9-109.9 244.8-245.2v-653.9c.2-135.3-109.4-245.1-244.7-245.3-135.4 0-244.9 109.8-244.8 245.1 0 0 0 .1 0 0" fill="#555" />
                      </g>
                    </svg>
            </div>
                ) : i === 1 ? (
                  <div
                    key={i}
                    className="flex items-center justify-center rounded-lg bg-gray-800/60"
                    style={{ filter: 'blur(0.5px)', opacity: 0.35 }}
                  >
                    {/* Jira logo SVG in grey */}
                    <svg className="w-8 h-8" viewBox="0 -30.632388516510233 255.324 285.95638851651023" xmlns="http://www.w3.org/2000/svg">
                      <path d="M244.658 0H121.707a55.502 55.502 0 0 0 55.502 55.502h22.649V77.37c.02 30.625 24.841 55.447 55.466 55.467V10.666C255.324 4.777 250.55 0 244.658 0z" fill="#555" />
                      <path d="M183.822 61.262H60.872c.019 30.625 24.84 55.447 55.466 55.467h22.649v21.938c.039 30.625 24.877 55.43 55.502 55.43V71.93c0-5.891-4.776-10.667-10.667-10.667z" fill="#555" />
                      <path d="M122.951 122.489H0c0 30.653 24.85 55.502 55.502 55.502h22.72v21.867c.02 30.597 24.798 55.408 55.396 55.466V133.156c0-5.891-4.776-10.667-10.667-10.667z" fill="#555" />
                    </svg>
          </div>
                ) : i === 2 ? (
                  <div
                    key={i}
                    className="flex items-center justify-center rounded-lg bg-gray-800/60"
                    style={{ filter: 'blur(0.5px)', opacity: 0.35 }}
                  >
                    {/* GitHub logo SVG in grey */}
                    <svg className="w-8 h-8" viewBox="0 0 1024 1024" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M8 0C3.58 0 0 3.58 0 8C0 11.54 2.29 14.53 5.47 15.59C5.87 15.66 6.02 15.42 6.02 15.21C6.02 15.02 6.01 14.39 6.01 13.72C4 14.09 3.48 13.23 3.32 12.78C3.23 12.55 2.84 11.84 2.5 11.65C2.22 11.5 1.82 11.13 2.49 11.12C3.12 11.11 3.57 11.7 3.72 11.94C4.44 13.15 5.59 12.81 6.05 12.6C6.12 12.08 6.33 11.73 6.56 11.53C4.78 11.33 2.92 10.64 2.92 7.58C2.92 6.71 3.23 5.99 3.74 5.43C3.66 5.23 3.38 4.41 3.82 3.31C3.82 3.31 4.49 3.1 6.02 4.13C6.66 3.95 7.34 3.86 8.02 3.86C8.7 3.86 9.38 3.95 10.02 4.13C11.55 3.09 12.22 3.31 12.22 3.31C12.66 4.41 12.38 5.23 12.3 5.43C12.81 5.99 13.12 6.7 13.12 7.58C13.12 10.65 11.25 11.33 9.47 11.53C9.76 11.78 10.01 12.26 10.01 13.01C10.01 14.08 10 14.94 10 15.21C10 15.42 10.15 15.67 10.55 15.59C13.71 14.53 16 11.53 16 8C16 3.58 12.42 0 8 0Z"
                        transform="scale(64)"
                        fill="#555"
                      />
                    </svg>
                  </div>
                ) : i === 3 ? (
                  <div
                    key={i}
                    className="flex items-center justify-center rounded-lg bg-gray-800/60"
                    style={{ filter: 'blur(0.5px)', opacity: 0.35 }}
                  >
                    {/* Confluence logo SVG in grey */}
                    <svg className="w-8 h-8" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3.015,23.087c-.289.472-.614,1.02-.891,1.456a.892.892,0,0,0,.3,1.212l5.792,3.564a.89.89,0,0,0,1.226-.29l.008-.013c.231-.387.53-.891.855-1.43,2.294-3.787,4.6-3.323,8.763-1.336l5.743,2.731A.892.892,0,0,0,26,28.559l.011-.024L28.766,22.3a.891.891,0,0,0-.445-1.167c-1.212-.57-3.622-1.707-5.792-2.754C14.724,14.586,8.09,14.831,3.015,23.087Z" fill="#666" />
                      <path d="M28.985,8.932c.289-.472.614-1.02.891-1.456a.892.892,0,0,0-.3-1.212L23.785,2.7a.89.89,0,0,0-1.236.241.584.584,0,0,0-.033.053c-.232.387-.53.891-.856,1.43-2.294,3.787-4.6,3.323-8.763,1.336L7.172,3.043a.89.89,0,0,0-1.187.421l-.011.024L3.216,9.726a.891.891,0,0,0,.445,1.167c1.212.57,3.622,1.706,5.792,2.753C17.276,17.433,23.91,17.179,28.985,8.932Z" fill="#666" />
                    </svg>
                  </div>
                ) : i === 4 ? (
                  <div
                    key={i}
                    className="flex items-center justify-center rounded-lg bg-gray-800/60"
                    style={{ filter: 'blur(0.5px)', opacity: 0.35 }}
                  >
                    {/* Linear logo SVG in grey */}
                    <svg className="w-8 h-8" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
              <path
                        d="M1.22541 61.5228c-.2225-.9485.90748-1.5459 1.59638-.857L39.3342 97.1782c.6889.6889.0915 1.8189-.857 1.5964C20.0515 94.4522 5.54779 79.9485 1.22541 61.5228ZM.00189135 46.8891c-.01764375.2833.08887215.5599.28957165.7606L52.3503 99.7085c.2007.2007.4773.3075.7606.2896 2.3692-.1476 4.6938-.46 6.9624-.9259.7645-.157 1.0301-1.0963.4782-1.6481L2.57595 39.4485c-.55186-.5519-1.49117-.2863-1.648174.4782-.465915 2.2686-.77832 4.5932-.92588465 6.9624ZM4.21093 29.7054c-.16649.3738-.08169.8106.20765 1.1l64.77602 64.776c.2894.2894.7262.3742 1.1.2077 1.7861-.7956 3.5171-1.6927 5.1855-2.684.5521-.328.6373-1.0867.1832-1.5407L8.43566 24.3367c-.45409-.4541-1.21271-.3689-1.54074.1832-.99132 1.6684-1.88843 3.3994-2.68399 5.1855ZM12.6587 18.074c-.3701-.3701-.393-.9637-.0443-1.3541C21.7795 6.45931 35.1114 0 49.9519 0 77.5927 0 100 22.4073 100 50.0481c0 14.8405-6.4593 28.1724-16.7199 37.3375-.3903.3487-.984.3258-1.3542-.0443L12.6587 18.074Z"
                        fill="#666"
              />
            </svg>
                      </div>
                ) : i === 5 ? (
                  <div
                    key={i}
                    className="flex items-center justify-center rounded-lg bg-gray-800/60"
                    style={{ filter: 'blur(0.5px)', opacity: 0.35 }}
                  >
                    {/* Datadog logo SVG in grey */}
                    <svg className="w-8 h-8" viewBox="0 0 800.5 907.77" xmlns="http://www.w3.org/2000/svg">
                      <path d="M303.36,238.61c31.36-21.37,71.76-12.97,65-6.53c-12.89,12.28,4.26,8.65,6.11,31.31c1.36,16.69-4.09,25.88-8.78,31.11c-9.79,1.28-21.69,3.67-36.02,8.33c-8.48,2.76-15.85,5.82-22.31,8.9c-1.7-1.11-3.55-2.47-5.74-4.36C279.5,288.19,280.24,254.37,303.36,238.61 M490.68,370.72c5.69-4.41,31.55-12.72,55.49-15.55c12.57-1.48,30.49-2.34,34.31-0.2c7.59,4.19,7.59,17.16,2.39,29.14c-7.57,17.4-18.27,36.63-30.39,38.21c-19.77,2.61-38.46-8.09-59.8-24.03C485.06,392.56,480.38,378.68,490.68,370.72 M526.75,201.27c29.19,13.58,25.37,39.42,26.18,54.6c0.22,4.36,0.15,7.3-0.22,9.32c-4.04-2.19-10.43-3.8-20.56-3.35c-2.96,0.12-5.84,0.47-8.63,0.91c-10.77-5.77-17.21-17.06-23.1-29.06c-0.54-1.11-0.96-2.1-1.36-3.06c-0.17-0.44-0.35-0.91-0.52-1.31c-0.07-0.22-0.12-0.39-0.2-0.59c-3.23-10.25-1.06-12.3,0.3-15.46c1.41-3.23,6.68-5.89-1.11-8.58c-0.67-0.25-1.5-0.39-2.44-0.57C500.25,197.72,515.7,196.17,526.75,201.27 M367.62,510.22c-31.45-20.19-63.99-49.15-78.22-65.18c-2.39-1.8-2-9.79-2-9.79c12.84,9.98,66.11,48.04,122.44,65.42c19.87,6.14,50.36,8.46,76.81-6.53c20.21-11.46,44.54-31.43,59.06-52.01l2.66,4.61c-0.1,3.06-6.78,17.97-10.18,23.96c6.14,3.53,10.72,4.49,17.55,6.36l46.64-7.27c16.74-27.04,28.74-70.65,15.95-112.16c-7.3-23.81-45.36-71.22-48.09-73.83c-9.56-9.19,1.6-44.69-17.35-83.42C532.86,159.41,480.67,116.69,458,98.1c6.68,4.88,47.82,21.47,67,44.62c1.8-2.39,2.54-14.82,4.19-17.97c-16.47-21.57-17.75-59.95-17.75-70.21c0-18.81-9.56-40.13-9.56-40.13s16.47,13.04,20.73,35.5c5.03,26.6,15.75,47.55,29.93,65.28c26.84,33.43,51.08,50.58,63.33,38.23C630.53,138.58,601,72.2,563.28,35.15C519.25-8.09,507.74-2.52,481.91,6.7c-20.61,7.35-31.75,65.87-85.47,64.71c-9.1-1.06-32.54-1.63-44.13-1.53c6.04-8.43,11.22-14.94,11.22-14.94s-18.02,7.25-33.38,16.44l-1.18-1.77c5.18-10.92,10.75-17.82,10.75-17.82s-14.4,8.65-27.54,19.01c2.39-13.02,11.44-21.27,11.44-21.27s-18.19,3.28-41.36,28.77c-26.33,7.2-32.66,11.93-53.64,21.22c-34.12-7.44-50.21-19.45-65.55-41.56c-11.68-16.89-32.47-19.45-53.71-10.72c-30.97,12.8-70.14,30.33-70.14,30.33s12.77-0.52,26.08,0.05c-18.22,6.9-35.72,16.39-35.72,16.39s8.53-0.3,19.06-0.12c-7.27,6.04-11.29,8.92-18.22,13.51c-16.66,12.1-30.17,26.08-30.17,26.08s11.31-5.15,21.47-8.04c-7.1,16.27-21.18,28.25-18.59,48.17c2.49,18.19,24.82,55.66,53.64,78.66c2.49,2,41.86,38.43,71.56,23.47c29.68-14.94,41.39-28.25,46.27-48.66c5.74-23.44,2.47-41.17-9.79-92.05c-4.04-16.79-14.57-51.37-19.65-67.91l1.13-0.81c9.71,20.49,34.56,74.5,44.57,110.78c15.63,56.57,10.75,85.27,3.6,95.79c-21.57,31.73-76.84,35.92-101.98,18.34c-3.85,60.91,9.76,87.73,14.37,101.24c-2.29,15.53,7.77,44.37,7.77,44.37s1.13-13.11,5.74-20.02c1.23,15.41,9,33.72,9,33.72s-0.47-11.31,3.06-21.08c4.98,8.43,8.63,10.43,13.34,16.76c4.71,16.47,14.15,28.5,14.15,28.5s-1.53-8.83-0.69-18.02c23.05,22.14,27.02,54.45,29.31,79.28c6.46,68.26-107.63,122.54-129.74,165.24c-16.76,25.29-26.8,65.3,1.58,88.89c68.6,56.97,42.25,72.65,76.59,97.69c47.11,34.34,106.05,18.96,126.11-8.97c27.93-38.92,20.76-75.63,10.38-109.97c-8.11-26.85-30.15-71.46-57.41-88.72c-27.86-17.65-54.95-20.95-77.9-18.59l2.12-2.44c33.01-6.56,67.52-2.96,92.49,13.14c28.35,18.22,54.28,49.47,67.84,97.37c15.38-2.19,17.55-3.18,31.63-5.18l-31.7-246.76L367.62,510.22z M385.94,819.52l-3.65-34.22l71.29-108.74l80.93,23.64l69.59-116.23L687.52,639l63.38-132.92l22.53,242.07L385.94,819.52z M774.27,456.51l-254.72,46.17c-6.31,8.13-21.91,22.41-29.41,26.13c-32.17,16.2-53.91,11.51-72.7,6.63c-12.08-3.06-19.08-4.78-29.11-9.29l-62.17,8.53l37.74,314.87l436.35-78.66L774.27,456.51z" fill="#666" />
                    </svg>
                      </div>
                ) : i === 6 ? (
                  <div
                    key={i}
                    className="flex items-center justify-center rounded-lg bg-gray-800/60"
                    style={{ filter: 'blur(0.5px)', opacity: 0.35 }}
                  >
                    {/* PagerDuty logo SVG in grey */}
                    <svg className="w-8 h-8" viewBox="0 0 256 372" xmlns="http://www.w3.org/2000/svg">
                      <path d="M54.5538972,272.557214 L54.5538972,371.475954 L0,371.475954 L0,272.557214 L54.5538972,272.557214 Z M109.046548,0.000774792703 C155.791517,0.0522613599 176.052007,2.70434494 204.842454,18.2553897 C236.470978,35.2371476 256,68.9883914 256,111.018242 C256,150.076285 240.079602,183.827529 209.512438,203.993367 C181.492537,222.6733 149.651741,225.220564 107.197347,225.220564 L107.197347,225.220564 L0,225.220564 L0,0 Z M117.785558,47.7544491 L116.112769,47.761194 L54.5538972,48.185738 L54.5538972,178.096186 L119.721393,178.096186 C165.359867,178.096186 200.172471,159.840796 200.172471,111.655058 C200.172471,66.8656716 172.15257,47.3366501 116.112769,47.761194 Z" fill="#666"></path>
                    </svg>
                    </div>
                ) : i === 7 ? (
                  <div
                    key={i}
                    className="flex items-center justify-center rounded-lg bg-gray-800/60"
                    style={{ filter: 'blur(0.5px)', opacity: 0.35 }}
                  >
                    {/* iMessage logo SVG in grey */}
                    <svg className="w-8 h-8" viewBox="0 0 66.145836 66.145836" xmlns="http://www.w3.org/2000/svg">
                      <rect
                        ry="14.567832"
                        rx="14.567832"
                        y="0"
                        x="0"
                        height="66.145836"
                        width="66.145836"
                        fill="#666"
                      />
                      <path
                        d="m 33.735687,11.45046 a 24.278298,20.222157 0 0 0 -24.278105,20.22202 24.278298,20.222157 0 0 0 11.79463,17.31574 27.365264,20.222157 0 0 1 -4.245218,5.94228 23.85735,20.222157 0 0 0 9.86038,-3.87367 24.278298,20.222157 0 0 0 6.868313,0.83768 24.278298,20.222157 0 0 0 24.2781059,-20.22203 24.278298,20.222157 0 0 0 -24.2781059,-20.22202 z"
                        fill="#121926"
                      />
                    </svg>
                      </div>
                ) : i === 8 ? (
                  <div
                    key={i}
                    className="flex items-center justify-center rounded-lg bg-gray-800/60"
                    style={{ filter: 'blur(0.5px)', opacity: 0.35 }}
                  >
                    {/* Sentry logo SVG in grey */}
                    <svg className="w-8 h-8" viewBox="0 0 256 227" xmlns="http://www.w3.org/2000/svg">
                      <path d="M148.367708,12.4025287 C144.036807,5.21480737 136.258026,0.820118864 127.866362,0.820118864 C119.474697,0.820118864 111.695916,5.21480737 107.365016,12.4025287 L73.6403017,70.165071 C126.066153,96.3390588 160.689085,148.341727 164.615024,206.806542 L140.93597,206.806542 C137.017513,156.694333 106.874845,112.396698 61.6982677,90.3588968 L30.4849684,144.32869 C55.8497707,155.704426 73.6778379,179.211206 77.7918243,206.704035 L23.4120041,206.704035 C22.1018479,206.611361 20.9266153,205.864669 20.2861278,204.71799 C19.6456403,203.571311 19.6261529,202.179068 20.2342955,201.014912 L35.3027847,175.388229 C30.1976229,171.128798 24.3630321,167.829476 18.0816541,165.65009 L3.16692493,191.276772 C0.0305635285,196.656417 -0.818661742,203.068719 0.809210488,209.079324 C2.43708272,215.08993 6.40620885,220.197261 11.8287436,223.258872 C15.3657216,225.251729 19.3523095,226.310116 23.4120041,226.334074 L97.8831433,226.334074 C100.696274,191.620878 85.1423372,157.966047 56.8804514,137.614499 L68.7199787,117.113153 C104.398813,141.618242 124.473737,183.151896 121.510945,226.334074 L184.603837,226.334074 C187.593899,160.904124 155.557278,98.8221906 100.497065,63.3483734 L124.432386,22.3456815 C125.542508,20.4856859 127.944329,19.8680747 129.81399,20.9618406 C132.530418,22.4481882 233.807067,199.169791 235.703442,201.219925 C236.383476,202.439289 236.358897,203.929352 235.639016,205.125624 C234.919136,206.321896 233.614065,207.041397 232.218213,207.011555 L207.821611,207.011555 C208.129131,213.537817 208.129131,220.046994 207.821611,226.539592 L232.32072,226.539592 C238.604421,226.580218 244.643414,224.105731 249.091568,219.667205 C253.539722,215.228679 256.027289,209.195062 256,202.911286 C256.002825,198.802186 254.905596,194.767215 252.822066,191.225519 L148.367708,12.4025287 Z" fill="#666" />
                    </svg>
                    </div>
                ) : i === 9 ? (
                  <div
                    key={i}
                    className="flex items-center justify-center rounded-lg bg-gray-800/60"
                    style={{ filter: 'blur(0.5px)', opacity: 0.35 }}
                  >
                    {/* Gmail logo SVG in grey */}
                    <svg className="w-8 h-8" viewBox="52 42 88 66" xmlns="http://www.w3.org/2000/svg">
                      <path fill="#666" d="M58 108h14V74L52 59v43c0 3.32 2.69 6 6 6" />
                      <path fill="#666" d="M120 108h14c3.32 0 6-2.69 6-6V59l-20 15" />
                      <path fill="#666" d="M120 48v26l20-15v-8c0-7.42-8.47-11.65-14.4-7.2" />
                      <path fill="#666" d="M72 74V48l24 18 24-18v26L96 92" />
                      <path fill="#666" d="M52 51v8l20 15V48l-5.6-4.2c-5.94-4.45-14.4-.22-14.4 7.2" />
                    </svg>
                  </div>
                ) : i === 10 ? (
                  <div
                    key={i}
                    className="flex items-center justify-center rounded-lg bg-gray-800/60"
                    style={{ filter: 'blur(0.5px)', opacity: 0.35 }}
                  >
                    {/* Terraform logo SVG in grey */}
                    <svg className="w-8 h-8" viewBox="0 0 128 128" xmlns="http://www.w3.org/2000/svg">
                      <g fillRule="evenodd">
                        <path d="M77.941 44.5v36.836L46.324 62.918V26.082zm0 0" fill="#666"/>
                        <path d="M81.41 81.336l31.633-18.418V26.082L81.41 44.5zm0 0" fill="#666"/>
                        <path d="M11.242 42.36L42.86 60.776V23.941L11.242 5.523zm0 0M77.941 85.375L46.324 66.957v36.82l31.617 18.418zm0 0" fill="#666"/>
                      </g>
                    </svg>
                  </div>
                ) : i === 11 ? (
                  <div
                    key={i}
                    className="flex items-center justify-center rounded-lg bg-gray-800/60"
                    style={{ filter: 'blur(0.5px)', opacity: 0.35 }}
                  >
                    {/* AWS logo SVG in grey */}
                    <svg className="w-8 h-8" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path fill="#666" d="M 20.455078 5.4121094 C 20.104078 5.4121094 19.769219 5.4528281 19.449219 5.5488281 C 19.130219 5.6448281 18.858141 5.7808437 18.619141 5.9648438 C 18.380141 6.1408438 18.188922 6.3631406 18.044922 6.6191406 C 17.900922 6.8741406 17.828125 7.1708594 17.828125 7.5058594 C 17.828125 7.9208594 17.963562 8.3111094 18.226562 8.6621094 C 18.489563 9.0211094 18.920906 9.2935625 19.503906 9.4765625 L 20.660156 9.8359375 C 21.051156 9.9639375 21.322797 10.098 21.466797 10.25 C 21.610797 10.402 21.681641 10.584594 21.681641 10.808594 C 21.681641 11.135594 21.538578 11.391406 21.267578 11.566406 C 20.996578 11.742406 20.603562 11.830078 20.101562 11.830078 C 19.782563 11.830078 19.471344 11.798375 19.152344 11.734375 C 18.841344 11.670375 18.544438 11.574078 18.273438 11.455078 C 18.193438 11.423078 18.122219 11.391 18.074219 11.375 C 18.026219 11.359 17.736328 11.205078 17.736328 11.205078 L 17.755859 12.287109 C 17.756859 12.287109 17.915969 12.381125 18.042969 12.453125 C 18.250969 12.573125 18.561937 12.677438 18.960938 12.773438 C 19.359938 12.869438 19.774453 12.917969 20.189453 12.917969 C 20.596453 12.917969 20.971078 12.860047 21.330078 12.748047 C 21.673078 12.644047 21.969797 12.502734 22.216797 12.302734 C 22.463797 12.110734 22.654828 11.870609 22.798828 11.599609 C 22.934828 11.328609 23.007813 11.016828 23.007812 10.673828 C 23.007812 10.258828 22.887625 9.8836406 22.640625 9.5566406 C 22.393625 9.2296406 21.977344 8.9740156 21.402344 8.7910156 L 20.269531 8.4316406 C 19.846531 8.2956406 19.552578 8.1441875 19.392578 7.9921875 C 19.232578 7.8401875 19.152344 7.6497813 19.152344 7.4257812 C 19.152344 7.0987813 19.280344 6.8677969 19.527344 6.7167969 C 19.774344 6.5647969 20.134844 6.4921875 20.589844 6.4921875 C 21.156844 6.4921875 21.668047 6.5976875 22.123047 6.8046875 C 22.259047 6.8686875 22.597656 7.0117187 22.597656 7.0117188 L 22.601562 5.96875 C 22.601562 5.96875 22.409828 5.8686875 22.298828 5.8046875 C 22.218828 5.7566875 22.107844 5.7081563 21.964844 5.6601562 C 21.820844 5.6121562 21.669766 5.5732031 21.509766 5.5332031 C 21.341766 5.5012031 21.174234 5.4693125 20.990234 5.4453125 C 20.814234 5.4213125 20.631078 5.4121094 20.455078 5.4121094 z M 3.9941406 5.4296875 C 3.5551406 5.4296875 3.1390469 5.4780312 2.7480469 5.5820312 C 2.3560469 5.6770313 2.0211875 5.7974062 1.7421875 5.9414062 C 1.6301875 5.9974063 1.4648438 6.0976563 1.4648438 6.0976562 L 1.4726562 7.1582031 C 1.4726562 7.1582031 1.9091094 6.9544375 2.0371094 6.8984375 C 2.3161094 6.7864375 2.6053906 6.6999062 2.9003906 6.6289062 C 3.1953906 6.5569062 3.4827188 6.5234375 3.7617188 6.5234375 C 4.3767188 6.5234375 4.8075 6.643625 5.0625 6.890625 C 5.3095 7.137625 5.4375 7.5609688 5.4375 8.1679688 L 5.4375 8.7519531 C 5.1185 8.6799531 4.8155313 8.6142188 4.5195312 8.5742188 C 4.2245313 8.5342188 3.9448281 8.5117188 3.6738281 8.5117188 C 2.8518281 8.5117188 2.20575 8.7197656 1.71875 9.1347656 C 1.23275 9.5497656 0.9921875 10.099344 0.9921875 10.777344 C 0.9921875 11.416344 1.1910313 11.926547 1.5820312 12.310547 C 1.9730313 12.693547 2.5096875 12.886719 3.1796875 12.886719 C 4.1216875 12.886719 4.9023906 12.518203 5.5253906 11.783203 C 5.6133906 11.967203 5.6944375 12.119859 5.7734375 12.255859 C 5.8534375 12.383859 5.9495469 12.510859 6.0605469 12.630859 C 6.1405469 12.702859 6.2773438 12.814453 6.2773438 12.814453 L 7.2734375 12.132812 C 7.2734375 12.132812 7.1454688 11.976109 7.1054688 11.912109 C 6.9934687 11.704109 6.9145625 11.513891 6.8515625 11.337891 C 6.7955625 11.161891 6.7460938 10.626953 6.7460938 10.626953 L 6.7460938 8.0390625 C 6.7460938 7.1610625 6.5239844 6.5071719 6.0839844 6.0761719 C 5.6379844 5.6461719 4.9441406 5.4296875 3.9941406 5.4296875 z M 17.248047 5.6230469 L 15.861328 5.6289062 L 14.384766 11.359375 L 13.03125 5.6308594 L 11.646484 5.6328125 L 10.300781 11.296875 L 8.8574219 5.6289062 L 7.4511719 5.6308594 L 9.5820312 12.693359 L 11 12.699219 L 12.318359 7.1621094 L 13.669922 12.701172 L 15.052734 12.705078 L 17.248047 5.6230469 z M 3.9707031 9.4609375 C 4.2177031 9.4609375 4.4657031 9.4777656 4.7207031 9.5097656 C 4.9757031 9.5417656 5.222125 9.5885312 5.453125 9.6445312 L 5.453125 9.9804688 C 5.453125 10.251469 5.4210469 10.482828 5.3730469 10.673828 C 5.3250469 10.865828 5.2305156 11.033547 5.1035156 11.185547 C 4.8875156 11.424547 4.6327031 11.599312 4.3457031 11.695312 C 4.0587031 11.791313 3.7864375 11.839844 3.5234375 11.839844 C 3.1564375 11.839844 2.8765469 11.743922 2.6855469 11.544922 C 2.4855469 11.352922 2.390625 11.074219 2.390625 10.699219 C 2.390625 10.300219 2.51825 9.99725 2.78125 9.78125 C 3.04425 9.56525 3.4357031 9.4609375 3.9707031 9.4609375 z M 22.236328 14.361328 C 21.396203 14.373203 20.404297 14.560891 19.654297 15.087891 C 19.423297 15.255891 19.46275 15.479266 19.71875 15.447266 C 20.57275 15.343266 22.457969 15.119781 22.792969 15.550781 C 23.127969 15.973781 22.416656 17.754922 22.097656 18.544922 C 22.001656 18.783922 22.210781 18.878312 22.425781 18.695312 C 23.829781 17.514312 24.195203 15.048453 23.908203 14.689453 C 23.764703 14.513953 23.076453 14.349453 22.236328 14.361328 z M 0.17578125 14.837891 C -4.6875e-05 14.859594 -0.078578125 15.072984 0.10742188 15.240234 C 3.2364219 18.066234 7.3795625 19.765625 11.976562 19.765625 C 15.257563 19.765625 19.071266 18.735875 21.697266 16.796875 C 22.128266 16.476875 21.753453 15.989453 21.314453 16.189453 C 18.369453 17.434453 15.169859 18.042969 12.255859 18.042969 C 7.9378594 18.042969 3.7629062 16.852672 0.37890625 14.888672 C 0.30515625 14.844672 0.23439062 14.830656 0.17578125 14.837891 z"/>
                    </svg>
                </div>
                ) : (
                  <div
                    key={i}
                    className="flex items-center justify-center rounded-lg bg-gray-800/60"
                    style={{ filter: 'blur(0.5px)', opacity: 0.35 }}
                  >
                    {/* Placeholder for app icon - replace with SVGs as needed */}
                    <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                      <rect width="32" height="32" rx="8" fill="#2D2D2D" />
                      <text x="16" y="21" textAnchor="middle" fontSize="14" fill="#666" fontFamily="sans-serif">App</text>
                    </svg>
                  </div>
                )
              ))}
                  </div>
            {/* Centered main icon with glow */}
            <div className="relative z-10 flex items-center justify-center">
              <div className="rounded-2xl bg-gradient-to-br from-pink-500 to-purple-500 shadow-xl flex items-center justify-center w-20 h-20" style={{ boxShadow: '0 0 40px 10px rgba(236,72,153,0.25)' }}>
                {/* Main icon image - TiccAIlogoWhite.png */}
                <img src={TiccAIlogoWhite} alt="TiccAI Logo" className="w-12 h-12 object-contain" />
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Smart Escalation",
      description: "We don't just escalate. TiccAI smartly routes tickets based on who's available, who's solved it before, and who's got the time.",
      mockup: (
        <div className="w-full h-[300px] bg-[#070d17] p-4 flex flex-col rounded-t-2xl border-2 border-b-0 border-white/10 shadow-[0_4px_24px_rgba(0,0,0,0.10)] relative overflow-hidden" style={{ boxShadow: '0 2px 24px 0 rgba(0,0,0,0.10)' }}>
          <div className="flex flex-col gap-4 h-full justify-center">
            {/* Approved Card */}
            <div className="flex items-center bg-[#0A0F1A] rounded-xl px-6 py-4 shadow border border-white/10 mb-1">
              <img src="/jessica.png" alt="Jessica Chen" className="w-12 h-12 rounded-full object-cover border-2 border-white/10 mr-4" />
              <div className="flex-1">
                <div className="text-white font-semibold text-lg leading-tight">Jessica Chen</div>
                <div className="text-gray-400 text-sm">Site Reliability Engineer</div>
            </div>
              <div className="ml-auto">
                <span className="px-4 py-1 rounded-lg border border-yellow-400 text-yellow-200 bg-yellow-800/30 text-sm font-medium">Busy</span>
          </div>
            </div>
            {/* Focused Card (Needs Approval) */}
            <div className="relative bg-[#0A0F1A] rounded-xl px-6 pt-4 pb-0 shadow-lg border border-white/15">
              <div className="flex items-center">
                <img src="/tejas.jpg" alt="Yusuf Hussain" className="w-12 h-12 rounded-full object-cover border-2 border-white/10 mr-4" />
                <div className="flex-1">
                  <div className="text-white font-semibold text-lg leading-tight">Alex Thompson</div>
                  <div className="text-gray-400 text-sm">Backend Engineer</div>
          </div>
                <div className="ml-auto">
                  <span className="px-4 py-1 rounded-lg border border-emerald-500 text-emerald-300 bg-emerald-900/20 text-sm font-medium">Best Fit</span>
                    </div>
                </div>
              {/* Divider */}
              <div className="h-px bg-gradient-to-r from-transparent via-white/20 to-transparent my-4" />
              {/* Action Area */}
              <div className="flex flex-col sm:flex-row items-center gap-4 pb-4">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <svg width="20" height="20" fill="none" viewBox="0 0 20 20" className="text-gray-400"><path d="M10 2.5a7.5 7.5 0 1 1 0 15a7.5 7.5 0 0 1 0-15zm0 1.5a6 6 0 1 0 0 12a6 6 0 0 0 0-12zm-2.25 5.25a.75.75 0 0 1 .75.75v1.25h2.5V10a.75.75 0 0 1 1.5 0v1.25a.75.75 0 0 1-.75.75h-3.5A.75.75 0 0 1 7 11.25V9.75a.75.75 0 0 1 .75-.75z" fill="currentColor"/></svg>
                  <span className="text-gray-400 text-sm truncate">Approve for making a payment</span>
                    </div>
                <div className="flex gap-2 ml-auto">
                  <button className="px-5 py-2 rounded-lg bg-[#181B20] text-gray-300 font-medium text-base cursor-default select-none" style={{ pointerEvents: 'none' }}>Cancel</button>
                  <button className="px-5 py-2 rounded-lg bg-gradient-to-r from-pink-500 via-pink-400 to-pink-600 text-white font-semibold text-base shadow-lg cursor-default select-none" style={{ pointerEvents: 'none', boxShadow: '0 0 15px 2px rgba(236,72,153,0.15), 0 0 5px 0 rgba(236,72,153,0.10)' }}>Approve</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    }
  ];

  const faqs = [
    {
      question: "How do I know TiccAI won't break production?",
      answer: "Every action is first dry‑run in a sandbox. Anything that could change prod must pass your YAML policies and human approval. Full audit logs mean you can always see what ran, why, and how to roll it back."
    },
    {
      question: "Our stack uses tools you don't list, can you integrate?",
      answer: "Yes. Every team is different; tell us what you use and we'll build a connector for it."
    },
    {
      question: "We don't have good runbooks, can TiccAI still help?",
      answer: "Yes. It scans your repos, previous tickets, and configs, then drafts starter runbooks you can tweak. As you accept or reject its edits, the system learns your way of doing things and keeps the docs fresh."
    },
    {
      question: "How is TiccAI different than other incident management tools, like PagerDuty?",
      answer: "PagerDuty notifies; TiccAI notifies and resolves. When an alert triggers, TiccAI gathers context, sandbox‑tests a fix, and—within your policies—clears routine issues, acting as your first‑responder SRE."
    },
    {
      question: "What happens to our data and secrets?",
      answer: "Everything stays in your cloud. We encrypt at rest, offer BYOK for LLM calls, and provide single‑tenant or self‑hosted options. No training data ever leaves your account."
    }
  ];

  return (
    <div className="min-h-screen bg-[#070d17] relative overflow-x-hidden">
      {/* Hero gradient glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[600px] pointer-events-none">
        {/* <img
          src="/bgv7.png"
          alt=""
          className="absolute z-0 left-1/2 top-0 -translate-x-1/2
                    w-[1200px] h-auto select-none pointer-events-none"
        />         */}
        <div
          className="absolute inset-0 opacity-[0.5]"
          style={{
            background: `
              radial-gradient(circle at 45% 0%,
                /* bright edge—low alpha so it's subtle */  
                rgba(139,  92, 246, 0.30)  0%,
                /* mid-tone fade */  
                rgba(101,  62, 200, 0.20) 20%,
                /* near-background tint */  
                rgba( 50,  35, 120, 0.12) 40%,
                /* almost gone */  
                rgba(  9,  16,  28, 0.06) 60%,
                /* fully transparent */  
                transparent               80%
              )
            `,
            filter: 'blur(0px)',
            transform: 'translateZ(0)',
            willChange: 'transform'
          }}
        />
      </div>

      {/* Background gradients */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Hero section gradient */}
        <div
          className="absolute top-20 -left-80 w-[600px] h-[600px] rounded-full animate-pulse"
          style={{
            background: 'radial-gradient(circle, rgba(255, 0, 128, 0.1), transparent)',
            filter: 'blur(100px)',
            transform: 'translateZ(0)',
            willChange: 'transform'
          }}
        />
        
        {/* Scattered background gradients */}
        <div
          className="absolute top-[20%] right-[10%] w-[400px] h-[400px] rounded-full animate-pulse"
          style={{
            background: 'radial-gradient(circle, rgba(99, 102, 241, 0.08), transparent)',
            filter: 'blur(80px)',
            animationDuration: '8s'
          }}
        />
        <div
          className="absolute top-[60%] left-[5%] w-[500px] h-[500px] rounded-full animate-pulse"
          style={{
            background: 'radial-gradient(circle, rgba(139, 92, 246, 0.08), transparent)',
            filter: 'blur(90px)',
            animationDuration: '10s'
          }}
        />
        <div
          className="absolute top-[40%] right-[15%] w-[300px] h-[300px] rounded-full animate-pulse"
          style={{
            background: 'radial-gradient(circle, rgba(67, 56, 202, 0.07), transparent)',
            filter: 'blur(70px)',
            animationDuration: '12s'
          }}
        />
        <div
          className="absolute top-[80%] right-[20%] w-[450px] h-[450px] rounded-full animate-pulse"
          style={{
            background: 'radial-gradient(circle, rgba(79, 70, 229, 0.08), transparent)',
            filter: 'blur(85px)',
            animationDuration: '9s'
          }}
        />
      </div>

      {/* Grid pattern overlay */}
      <div 
        className="absolute inset-0 pointer-events-none opacity-[0.225]"
        style={{
          backgroundImage: `
            linear-gradient(to right, rgba(255, 255, 255, 0.06) 1px, transparent 1px),
            linear-gradient(to bottom, rgba(255, 255, 255, 0.06) 1px, transparent 1px)
          `,
          backgroundSize: '40px 40px',
          maskImage: 'linear-gradient(to bottom, transparent, black 10%, black 90%, transparent)',
          WebkitMaskImage: 'linear-gradient(to bottom, transparent, black 10%, black 90%, transparent)'
        }}
      />

      {/* Replace the existing particles div with the new component */}
      <Particles />

      <div className="fixed top-0 left-0 right-0 z-50">
        <div className="mx-4 sm:mx-6 lg:mx-8 mt-4">
          <nav className="max-w-7xl mx-auto backdrop-blur-[6px] bg-[#070d17]/40 rounded-2xl border border-white/[0.1] font-sans">
            <div className="px-4 sm:px-6 lg:px-8">
              <div className="flex items-center justify-between h-16">
                <div className="flex items-center">
                  <div className="flex items-center gap-2">
                    <img src={TiccAIlogo} alt="TiccAI" className="h-7 w-7" />
                    <span className="text-2xl font-bold text-white">TiccAI</span>
                  </div>
                </div>

                <div className="hidden md:block">
                  <div className="flex items-center space-x-8">
                    <button 
                      onClick={() => {
                        const featuresSection = document.getElementById('features');
                        if (featuresSection) {
                          const yOffset = -50;
                          const y = featuresSection.getBoundingClientRect().top + window.pageYOffset + yOffset;
                          window.scrollTo({ top: y, behavior: 'smooth' });
                        }
                      }}
                      className="nav-button text-gray-300 font-sans"
                    >
                      <span>Features</span>
                    </button>
                    <button 
                      onClick={() => {
                        const howItWorksSection = document.getElementById('how-it-works');
                        if (howItWorksSection) {
                          const yOffset = 20;
                          const y = howItWorksSection.getBoundingClientRect().top + window.pageYOffset + yOffset;
                          window.scrollTo({ top: y, behavior: 'smooth' });
                        }
                      }}
                      className="nav-button text-gray-300 font-sans"
                    >
                      <span>Dashboard</span>
                    </button>
                    <button 
                      onClick={() => {
                        const integrationsSection = document.getElementById('integrations');
                        if (integrationsSection) {
                          const yOffset = -100; // Add offset to show more of the section
                          const y = integrationsSection.getBoundingClientRect().top + window.pageYOffset + yOffset;
                          window.scrollTo({ top: y, behavior: 'smooth' });
                        }
                      }} 
                      className="nav-button text-gray-300 font-sans"
                    >
                      <span>Integrations</span>
                    </button>
                    <button 
                      onClick={() => openAuthModal('signin')}
                      className="nav-button text-gray-300 font-sans"
                    >
                      <span>Sign in</span>
                    </button>
                    <button 
                      onClick={() => openAuthModal('signup')}
                      className="get-started-button bg-gradient-to-r from-pink-500 to-pink-600 text-white px-6 py-2 rounded-full font-sans"
                    >
                      <span>Get started</span>
                    </button>
                  </div>
                </div>

                <div className="md:hidden">
                  <button
                    onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                    className="text-gray-300 hover:text-white font-sans"
                  >
                    {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                  </button>
                </div>
              </div>

              {isMobileMenuOpen && (
                <div className="md:hidden pb-4">
                  <div className="px-2 space-y-1">
                    <button 
                      onClick={() => {
                        setIsMobileMenuOpen(false);
                        const featuresSection = document.getElementById('features');
                        if (featuresSection) {
                          const yOffset = -50;
                          const y = featuresSection.getBoundingClientRect().top + window.pageYOffset + yOffset;
                          window.scrollTo({ top: y, behavior: 'smooth' });
                        }
                      }}
                      className="nav-button block text-gray-300 py-2 font-sans"
                    >
                      <span>Features</span>
                    </button>
                    <button 
                      onClick={() => {
                        setIsMobileMenuOpen(false);
                        const howItWorksSection = document.getElementById('how-it-works');
                        if (howItWorksSection) {
                          const yOffset = 20;
                          const y = howItWorksSection.getBoundingClientRect().top + window.pageYOffset + yOffset;
                          window.scrollTo({ top: y, behavior: 'smooth' });
                        }
                      }}
                      className="nav-button block w-full text-left text-gray-300 py-2 font-sans"
                    >
                      <span>Dashboard</span>
                    </button>
                    <button 
                      onClick={() => {
                        setIsMobileMenuOpen(false);
                        const integrationsSection = document.getElementById('integrations');
                        if (integrationsSection) {
                          const yOffset = -100; // Add offset to show more of the section
                          const y = integrationsSection.getBoundingClientRect().top + window.pageYOffset + yOffset;
                          window.scrollTo({ top: y, behavior: 'smooth' });
                        }
                      }} 
                      className="nav-button block w-full text-left text-gray-300 py-2 font-sans"
                    >
                      <span>Integrations</span>
                    </button>
                    <button 
                      onClick={() => {
                        setIsMobileMenuOpen(false);
                        openAuthModal('signin');
                      }}
                      className="nav-button block w-full text-left text-gray-300 py-2 font-sans"
                    >
                      <span>Sign in</span>
                    </button>
                    <button 
                      onClick={() => {
                        setIsMobileMenuOpen(false);
                        openAuthModal('signup');
                      }}
                      className="get-started-button w-full bg-gradient-to-r from-pink-500 to-pink-600 text-white px-6 py-2 rounded-full mt-4 font-sans"
                    >
                      <span>Get started</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </nav>
        </div>
      </div>

      <main className="relative flex-1">
        {/* Hero Section - Increase bottom padding */}
        <div className="pt-16 pb-32 sm:pt-40 lg:pt-28">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Left side - Text content */}
              <div className="lg:text-left text-center font-sans overflow-visible sm:mt-0 mt-8">
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-normal font-sans">
                  <span className="header-text">Automate</span>{' '}
                  <span className="header-text header-text-delay-1 animate-in">your</span>
                  <br />
                  <span className="header-text header-text-delay-2">incident resolution</span>{' '}
                  <span className="header-text header-text-delay-3">with</span>{' '}
                  <span className="header-text header-text-delay-4 gradient-text gradient-fix">AI</span>{' '}
                  <span className="header-text header-text-delay-5 gradient-text gradient-fix">agents</span>
                </h1>
                <p className="mt-6 text-base sm:text-lg md:text-xl text-gray-400 font-sans lg:text-left text-center header-text header-text-delay-6">
                  TiccAI's domain-specific agents plug into any alert system, sandbox fixes, resolves routine alerts, and files the report, freeing your team to keep building.
                  
                  {/* 👉 keep all your original classes & children */}
                  <VideoModal
                    videoId="doYAWwUO19g"
                    className="ml-2 inline-flex items-center text-white hover:text-pink-500 transition-colors duration-200 tldr-link"
                  >
                    <span className="text-lg">Watch 1 min TL;DR</span>
                    <svg
                      className="w-5 h-5 ml-1 tldr-play-button"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </VideoModal>
                </p>
                <div className="mt-10 flex gap-4 lg:justify-start justify-center">
                  <button 
                    onClick={() => navigate('/pricing')}
                    className="hero-button header-text header-text-delay-7 bg-gradient-to-r from-pink-500 to-pink-600 text-white px-8 py-3 rounded-full text-lg font-medium h-12 font-sans"
                  >
                    Get started
                  </button>
                  
                  <div 
                    onClick={() => window.open('https://cal.com/ticcai/30m', '_blank')}
                    className="demo-card header-text-delay-8 h-12 font-sans cursor-pointer px-0"
                  >
                    <div className="demo-card-inner">
                      {/* Front side - TL;DR text */}
                      <div className="demo-card-front flex items-center gap-2">
                        Book demo
                      </div>

                      {/* Back side - Play text */}
                      <div className="demo-card-back flex items-center gap-2">
                        Let's talk!
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* Right side - AnimatedBeamDemo */}
              <div className="animated-beam-fade sm:-mt-20 lg:mt-0 hidden sm:block">
                <MemoizedAnimatedBeamDemo />
              </div>
            </div>
          </div>
        </div>

        {/* Stats Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-20 stats-section">
          <div className="text-center mb-8 stats-header">
            <div className="flex items-center justify-center gap-2">
              <BarChart className="w-4 h-4 text-gray-100 filter drop-shadow-[0_0_2px_rgba(255,255,255,0.6)] mb-3" />
              <p className="text-sm text-gray-400 font-['VCR_OSD_Mono'] font-normal mb-3">
                <span className="text-gray-100">Receipts?</span> We've got plenty of 'em.
              </p>
            </div>
          </div>
          <div className="flex items-center justify-center relative sm:flex-row flex-col sm:flex-wrap sm:flex-nowrap">
            {[
              {
                number: 65,
                label: "FASTER TICKET RESOLUTION"
              },
              {
                number: 1500,
                label: "AI-TRAINED INCIDENTS"
              },
              {
                number: 12,
                label: "INTEGRATIONS"
              }
            ].map((stat, index) => {
              const { count, elementRef } = useCountAnimation(stat.number, 2000, index * 200);
              return (
                <div key={index} className="flex items-center stat-slide-up" ref={elementRef}>
                  <div className="text-center px-8">
                    <div className="text-4xl font-bold text-white filter drop-shadow-[0_0_8px_rgba(255,255,255,0.3)] font-sans animate-[pulse-glow_3s_ease-in-out_infinite]">
                      {formatNumber(count)}
                    </div>
                    <div className="text-sm text-gray-400 mt-2 font-sans">{stat.label}</div>
                  </div>
                  {index < 2 && (
                    <div className="h-16 w-px bg-gradient-to-b from-transparent via-white/15 to-transparent hidden sm:block" />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Integrations section - Add more top margin */}
        <div id="integrations" className="sm:mt-32 mt-8">
          <Integrations />
        </div>

        {/* Features section - Increase top and bottom margins */}
        <div id="features" className="py-32 relative z-10 sm:-mt-40 mt-0 -mb-10">
          {/* Add layered glow effects */}
          <div className="absolute inset-0 overflow-hidden">
            {/* Primary glow layer */}
            <div
              className="absolute w-[900px] h-[900px] -left-[20%] top-[20%] opacity-[0.08]"
              style={{
                background: 'radial-gradient(circle at center, rgba(236, 72, 153, 0.25) 0%, rgba(139, 92, 246, 0.15) 35%, transparent 70%)',
                filter: 'blur(80px)',
                transform: 'translateZ(0)',
                willChange: 'transform'
              }}
            />
            {/* Secondary glow layer */}
            <div
              className="absolute w-[700px] h-[700px] -left-[15%] top-[25%] opacity-[0.07]"
              style={{
                background: 'radial-gradient(circle at center, rgba(147, 197, 253, 0.25) 0%, rgba(167, 139, 250, 0.15) 30%, transparent 60%)',
                filter: 'blur(90px)',
                transform: 'translateZ(0)',
                willChange: 'transform'
              }}
            />
            {/* Accent glow layer */}
            <div
              className="absolute w-[600px] h-[600px] -left-[10%] top-[15%] opacity-[0.06]"
              style={{
                background: 'radial-gradient(circle at center, rgba(99, 102, 241, 0.25) 0%, rgba(79, 70, 229, 0.15) 35%, transparent 65%)',
                filter: 'blur(75px)',
                transform: 'translateZ(0)',
                willChange: 'transform'
              }}
            />
          </div>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <div className="text-left mb-4 fade-up-on-scroll opacity-0">
              <div className="flex items-center gap-2">
                <Layers className="w-4 h-4 text-gray-300 filter drop-shadow-[0_0_2px_rgba(255,255,255,0.6)] mb-3" />
                <div className="text-sm text-gray-400 font-['VCR_OSD_Mono'] font-normal mb-3">FEATURES</div>
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight font-sans">
                Everything you need in one place.
              </h2>
              <p className="mt-3 text-lg text-gray-400 font-sans">
                It's 2025 - Your engineers should be building new features, not managing tickets manually.
              </p>
            </div>
            <div className="relative">
              <div className="fade-up-on-scroll opacity-0">
                <div 
                  className="carousel-container overflow-hidden relative w-screen -ml-[calc((100vw-100%)/2)] touch-pan-y overscroll-none"
                >
                  {/* Add vertical fading bar */}
                  <div className="absolute right-0 top-0 bottom-0 w-[200px] z-40 pointer-events-none" style={{
                    background: 'linear-gradient(90deg, rgba(10,13,23,0) 0%, rgba(7,13,23,1) 100%)'
                  }}></div>
                  
                  <div 
                    className="carousel-track flex z-30"
                    ref={carouselRef}
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                    style={{
                      transform: `translateX(-${scrollPosition}px)`,
                      transition: isScrolling ? 'none' : 'transform 600ms cubic-bezier(0.4, 0, 0.2, 1)',
                      paddingLeft: `calc((100vw - 100%) / 2 + ${responsivePadding}px)`
                    }}
                  >
                    {features.map((feature, index) => (
                      <div 
                        key={index}
                        className="carousel-item flex-shrink-0 cursor-pointer"
                        style={{ 
                          width: feature.title === "Smart Ticket Analysis" ? '900px' : 
                                 feature.title === "Automated Resolution" ? '700px' : '500px',
                          marginRight: '4px',
                          height: '500px'
                        }}
                        onClick={() => {
                          if (index < currentFeatureIndex) {
                            handlePrevClick();
                          } else if (index > currentFeatureIndex) {
                            handleNextClick();
                          }
                        }}
                      >
                        <div className="feature-card !bg-[#070d17] rounded-2xl border-2 border-white/[0.13] p-6 hover:border-white/[0.3] transition-all duration-300 relative h-full overflow-hidden">
                          <div className="p-6">
                            <h3 className="text-white font-semibold text-xl mb-3 font-sans">{feature.title}</h3>
                            <p className="text-gray-400 font-sans">{feature.description}</p>
                          </div>
                          {feature.mockup}
                          {/* Add fade effect at the bottom */}
                          <div className="absolute bottom-0 left-0 right-0 h-24 pointer-events-none">
                            <div className="absolute inset-0 bg-gradient-to-t from-[#070d17] via-[#070d17]/50 to-transparent"></div>
                            <div className="absolute inset-0 bg-gradient-to-t from-[#070d17] to-transparent opacity-40"></div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                {/* Replaces Section - Now inside the fade-up container */}
                <div className="mt-4 flex justify-between items-center mb-20">
                  <div className="flex items-center gap-4">
                    <button
                      onClick={handlePrevClick}
                      disabled={currentFeatureIndex === 0}
                      className="carousel-nav-button flex items-center justify-center -ml-4"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="m15 18-6-6 6-6"/>
                      </svg>
                    </button>
                    <div className="flex items-center gap-2 sm:flex hidden">
                      {features.map((_, index) => (
                        <div
                          key={index}
                          className={`w-2 h-2 rounded-full transition-all duration-300 ${
                            index === currentFeatureIndex ? 'bg-white/40' : 'bg-white/10'
                          }`}
                          onClick={() => {
                            setCurrentFeatureIndex(index);
                            setScrollPosition(index * 550);
                          }}
                        />
                      ))}
                    </div>
                    <button
                      onClick={handleNextClick}
                      disabled={currentFeatureIndex === features.length - 1}
                      className="carousel-nav-button flex items-center justify-center -mr-4"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="m9 18 6-6-6-6"/>
                      </svg>
                    </button>
                  </div>

                  <div className="flex items-center gap-2">
                    <p className="text-gray-400 text-sm mr-3 font-sans">Replaces</p>
                    <div className="h-4 w-px bg-white/10 mr-3"></div>
                    {/* PagerDuty Logo */}
                    <div>
                      <svg width="13.0px" height="18.5px" viewBox="0 0 256 372" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink" preserveAspectRatio="xMidYMid">
                        <title>PagerDuty</title>
                        <g>
                            <path d="M54.5538972,272.557214 L54.5538972,371.475954 L0,371.475954 L0,272.557214 L54.5538972,272.557214 Z M109.046548,0.000774792703 C155.791517,0.0522613599 176.052007,2.70434494 204.842454,18.2553897 C236.470978,35.2371476 256,68.9883914 256,111.018242 C256,150.076285 240.079602,183.827529 209.512438,203.993367 C181.492537,222.6733 149.651741,225.220564 107.197347,225.220564 L107.197347,225.220564 L0,225.220564 L0,0 Z M117.785558,47.7544491 L116.112769,47.761194 L54.5538972,48.185738 L54.5538972,178.096186 L119.721393,178.096186 C165.359867,178.096186 200.172471,159.840796 200.172471,111.655058 C200.172471,66.8656716 172.15257,47.3366501 116.112769,47.761194 Z" fill="#06AC38"></path>
                            </g>
                          </svg>
                    </div>

                    {/* Jira Logo */}
                    <div>
                      <svg width="18.5" height="18.5" viewBox="-24.5 0 305 305" xmlns="http://www.w3.org/2000/svg">
                        <defs>
                          <linearGradient x1="50%" y1="16.6199552%" x2="50%" y2="119.282511%" id="linearGradient-1">
                            <stop stopColor="#2684FF" offset="0%"/>
                            <stop stopColor="#0052CC" offset="82%"/>
                          </linearGradient>
                          <linearGradient x1="41.179673%" y1="31.1603357%" x2="67.7136955%" y2="78.6783874%" id="linearGradient-2">
                            <stop stopColor="#2684FF" offset="0%"/>
                            <stop stopColor="#0052CC" offset="62%"/>
                          </linearGradient>
                        </defs>
                        <g>
                          <circle fill="url(#linearGradient-1)" cx="127.996078" cy="76.0579131" r="76.0579131"/>
                          <path d="M121.515807,302.952652 C71.8432945,271.266206 30.5322167,228.083335 1.07656696,177.056341 C-0.0620320498,175.01097 -0.309636381,172.587443 0.391817304,170.354079 C1.09327099,168.120715 2.6820805,166.273968 4.78566922,165.246901 L62.3833377,136.980984 C66.4123421,135.016304 71.2746417,136.506664 73.5106445,140.391652 C102.630871,189.198451 145.690139,228.174512 197.147387,252.304221 C178.169747,271.425488 157.153981,288.409659 134.476348,302.952652 C130.510908,305.428568 125.481248,305.428568 121.515807,302.952652 Z" fill="url(#linearGradient-2)"/>
                          <path d="M134.476348,302.952652 C184.155284,271.274027 225.468036,228.089405 254.915589,177.056341 C256.057132,175.016287 256.31102,172.597018 255.617868,170.364422 C254.924715,168.131826 253.345322,166.281735 251.24912,165.246901 L193.608818,136.980984 C189.579813,135.016304 184.717514,136.506664 182.481511,140.391652 C153.367402,189.203464 110.306398,228.181097 58.8447689,252.304221 C77.8108513,271.438283 98.8280892,288.423643 121.515807,302.952652 C125.481248,305.428568 130.510908,305.428568 134.476348,302.952652 Z" fill="#2684FF"/>
                        </g>
                      </svg>
                    </div>

                    {/* Splunk On-call Logo */}
                    <div>
                      <svg width="18.5" height="18.5" viewBox="0 0 256 256" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid">
                        <path d="M0 128c0 70.4 57.6 128 128 128s128-57.6 128-128S198.4 0 128 0 0 57.6 0 128z" fill="currentColor" fillOpacity="0.2"/>
                        <path d="M191.36 130.56l-39.04 18.56-.64 6.4 30.08-8.96-14.72 17.92-1.28 5.76-18.56 7.68-1.92 5.12 8.32-3.2-21.76 24.32-1.92 19.84v18.56h-3.84v-17.92l-1.28-20.48-21.76-24.32 8.32 3.2-1.92-5.12-18.56-7.68-1.28-5.76-14.72-17.92 30.08 8.96-.64-6.4-39.04-18.56 8.96-14.08-18.56-24.96 51.2 23.04 1.28-7.04-14.72-23.04 1.92-3.84-7.04-26.88 26.88 26.88 1.92-14.72-8.32-21.76 10.88 3.2 9.6-26.88 9.6 26.88 10.88-3.2-8.32 21.76 1.92 14.72 26.88-26.88-7.04 26.88 1.92 3.84-14.72 23.04 1.28 7.04 51.2-23.04-18.56 24.96 7.04 14.08z" fill="white"/>
                      </svg>
                    </div>

                    {/* Incident.io Logo */}
                    <div>
                      <svg width="18.5" height="18.5" viewBox="0 0 256 346" xmlns="http://www.w3.org/2000/svg">
                        <path d="M96.7291013,344.73746 L96.7291013,298.160333 L157.698751,298.160333 L157.698751,345.15049 C214.061523,331.679343 256,280.685967 256,219.843404 C256,170.327406 220.86064,100.652345 180.113596,79.0953326 C185.594193,98.4283351 177.365355,123.623193 166.404162,121.415069 C162.273858,120.589008 161.844942,114.059949 161.177738,103.511787 C159.779789,81.4305432 157.269835,41.7160762 117.015251,2.84217094e-14 C113.806322,29.134533 67.6104541,88.4838326 37.5545454,127.118066 C33.4401267,132.392147 29.6434236,137.28497 26.2756368,141.653561 C9.2122009,164.147397 -0.0163462121,191.609857 0.000272079258,219.843404 C-0.120101094,278.702666 39.7125438,330.140355 96.7291013,344.753346 L96.7291013,344.73746 Z M161.876895,245.784894 C161.90625,254.829121 158.340661,263.51442 151.964788,269.929025 C145.588915,276.34363 136.925369,279.961754 127.881129,279.986993 C118.836889,279.961754 110.173344,276.34363 103.79747,269.929025 C97.4215973,263.51442 93.8560089,254.829121 93.8853634,245.784894 C93.8853634,237.984972 96.5702435,230.645739 100.875292,225.02217 L103.86182,221.161924 C111.85237,210.899706 124.116198,195.141005 124.97403,187.404627 C150.280089,204.497734 161.876895,232.631462 161.876895,245.784894 L161.876895,245.784894 Z" fill="#F25533"/>
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Glowing Effect Cards Section - Add more top margin */}
        <div className="sm:mt-0 mt-8">
          <GlowingEffectDemo />
        </div>

        <div className="py-16 relative z-10 mt-28">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col lg:grid lg:grid-cols-2 gap-6 items-start">
              <div className="lg:sticky lg:top-8 w-full lg:flex lg:flex-col lg:justify-center lg:items-start mb-16">
                <div className="text-left fade-up-on-scroll opacity-0">
                  <div className="flex items-center gap-2 mb-2">
                    <svg className="w-4 h-4 text-gray-300 filter drop-shadow-[0_0_2px_rgba(255,255,255,0.6)]" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M9.09 9C9.3251 8.33167 9.78915 7.76811 10.4 7.40913C11.0108 7.05016 11.7289 6.91894 12.4272 7.03871C13.1255 7.15849 13.7588 7.52152 14.2151 8.06353C14.6713 8.60553 14.9211 9.29152 14.92 10C14.92 12 11.92 13 11.92 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M12 17H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <div className="text-sm text-gray-400 font-['VCR_OSD_Mono'] font-normal">FAQ</div>
                  </div>
                  <h2 className="text-4xl lg:text-5xl font-bold text-white mb-4">
                    Frequently{' '}
                    <span className="relative inline-block">
                      <span className="float-in-on-scroll opacity-0">asked</span>
                    </span>
                    <br />
                    <div className="relative inline-flex items-center">
                      <span className="typing-text opacity-0">questions</span>
                    </div>
                  </h2>
                  <div className="mt-4">
                    <div className="inline-flex items-center gap-2 text-lg">
                      <span className="text-gray-400">
                        Still have questions?
                      </span>
                      <a 
                        href="/contact" 
                        className="text-pink-500 font-medium flex items-center gap-2 relative group"
                      >
                        <span className="relative text-pink-500 group-hover:bg-gradient-to-r group-hover:from-pink-500 group-hover:to-pink-600 group-hover:bg-clip-text group-hover:text-transparent">
                          Contact our team
                          <span className="absolute -bottom-1 left-0 w-full h-[2px] bg-gradient-to-r from-pink-500 to-pink-600 transform scale-x-0 transition-transform duration-300 group-hover:scale-x-100"></span>
                        </span>
                        <ChevronDown className="h-5 w-5 rotate-[-90deg] transition-transform duration-300 group-hover:translate-x-1" />
                      </a>
                    </div>
                  </div>
                </div>
              </div>
              <div className="space-y-4 w-full">
                {faqs.map((faq, index) => (
                  <div key={index}>
                    <button
                      className={`w-full text-left py-6 transition-all duration-300 ease-out fade-up-on-scroll opacity-0 ${
                        index === 0 ? 'pt-0' : ''
                      }`}
                      style={{ transitionDelay: `${index * 100}ms` }}
                      onClick={() => setOpenFaq(openFaq === faq.question ? null : faq.question)}
                    >
                      <div className="w-full flex items-center justify-between group">
                        <span className="text-lg text-white font-medium transition-all duration-300 ease-out group-hover:text-pink-500">{faq.question}</span>
                        <div className={`transition-transform duration-300 ease-out ${openFaq === faq.question ? '-rotate-180' : 'rotate-0'}`}>
                          <ChevronDown 
                            className={`h-5 w-5 transition-all duration-300 ease-out ${
                              openFaq === faq.question 
                                ? 'text-pink-500' 
                                : 'text-gray-400 group-hover:text-pink-500 group-hover:translate-y-0.5'
                            }`}
                          />
                        </div>
                      </div>
                      <div 
                        className={`grid transition-all duration-500 ease-in-out ${
                          openFaq === faq.question 
                            ? 'grid-rows-[1fr] opacity-100' 
                            : 'grid-rows-[0fr] opacity-0'
                        }`}
                      >
                        <div className="overflow-hidden">
                          <div className={`transform transition-all duration-500 ease-out ${
                            openFaq === faq.question 
                              ? 'translate-y-0 pt-4' 
                              : '-translate-y-2'
                          }`}>
                            <p className="text-gray-400 transition-all duration-300 ease-out">{faq.answer}</p>
                          </div>
                        </div>
                      </div>
                    </button>
                    {index < faqs.length - 1 && (
                      <div className="w-full h-px bg-white/[0.08] transition-all duration-300 ease-out"></div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />

      <AuthModal 
        isOpen={isAuthModalOpen}
        onClose={closeAuthModal}
        mode={isAuthModalOpen ? 'signup' : 'signin'}
      />
    </div>
  );
}

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/integrations" element={<Integrations />} />
        <Route path="/waitlist" element={<BetaWaitlist />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/security" element={<Security />} />
        <Route path="/pricing" element={<Pricing />} />
      </Routes>
    </Router>
  );
}

export default App;